// src/components/tpv/TpvManager.jsx

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCog,
    faHistory,
    faChartBar,
    faCheckCircle,
    faExclamationTriangle,
    faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import TpvConfiguration from './TpvConfiguration';
import Swal from 'sweetalert2';
import { tpvConfigurations, tpvStats } from '../../helpers/tpvHelper';

const TpvManager = ({ tiendaId, marcaId }) => {
    const [activeTab, setActiveTab] = useState('config');
    const [configurations, setConfigurations] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (tiendaId) {
            fetchData();
        }
    }, [tiendaId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [configurationsData, statsData] = await Promise.all([
                tpvConfigurations.getByTienda(tiendaId).catch(() => []),
                tpvStats.getByTienda(tiendaId).catch(() => null)
            ]);


            setConfigurations(configurationsData);
            setStats(statsData);
        } catch (error) {
            console.error('Error fetching TPV data:', error);
        } finally {
            setLoading(false);
        }
    };

    const tabs = [
        { id: 'config', label: 'Configuración', icon: faCog },
        { id: 'info', label: 'Información', icon: faInfoCircle },
        { id: 'stats', label: 'Estadísticas', icon: faChartBar }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Cargando configuración TPV...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow border p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Gestión TPV</h1>
                        <p className="text-gray-600">Configuración de terminales de punto de venta para integración con Agora</p>
                    </div>

                    {configurations.length > 0 && (
                        <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                            <span className="text-sm font-medium text-green-800">
                                {configurations.length} TPV{configurations.length > 1 ? 's' : ''} configurado{configurations.length > 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="mt-6">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                >
                                    <FontAwesomeIcon icon={tab.icon} />
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-lg shadow border">
                {activeTab === 'config' && (
                    <div className="p-6">
                        <TpvConfiguration
                            tiendaId={tiendaId}
                            marcaId={marcaId}
                            onConfigUpdated={fetchData}
                        />
                    </div>
                )}

                {activeTab === 'info' && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Información de Integración</h3>

                        <div className="space-y-6">
                            {/* Información del Webhook */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500 mt-1 mr-3 flex-shrink-0" />
                                    <div className="w-full">
                                        <h4 className="text-sm font-medium text-blue-800 mb-3">📋 Cómo Configurar en Agora Software</h4>

                                        <div className="space-y-4">
                                            {/* Paso 1 */}
                                            <div className="bg-white p-4 rounded-lg border">
                                                <h5 className="font-medium text-gray-800 mb-2">Paso 1: Acceder a Configuración de Webhooks</h5>
                                                <p className="text-sm text-gray-600 mb-2">
                                                    En tu software Agora, ve a <strong>Configuración → Webhooks → Configurar Webhook</strong>
                                                </p>
                                            </div>

                                            {/* Paso 2 */}
                                            <div className="bg-white p-4 rounded-lg border">
                                                <h5 className="font-medium text-gray-800 mb-2">Paso 2: Configurar la URL del Webhook</h5>
                                                <p className="text-sm text-gray-600 mb-2">Copia y pega esta URL en el campo "Webhook URL":</p>
                                                <div className="bg-gray-100 p-3 rounded border">
                                                    <code className="text-sm font-mono text-gray-800 break-all">
                                                        {process.env.NODE_ENV === 'production'
                                                            ? 'https://api.petitprime.com/api/webhooks/agora'
                                                            : 'http://localhost:3000/api/webhooks/agora'
                                                        }
                                                    </code>
                                                    <button
                                                        onClick={() => {
                                                            const url = process.env.NODE_ENV === 'production'
                                                                ? 'https://api.petitprime.com/api/webhooks/agora'
                                                                : 'http://localhost:3000/api/webhooks/agora';
                                                            navigator.clipboard.writeText(url);
                                                        }}
                                                        className="ml-2 px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                                                    >
                                                        Copiar
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Paso 3 */}
                                            <div className="bg-white p-4 rounded-lg border">
                                                <h5 className="font-medium text-gray-800 mb-2">Paso 3: Configurar Headers</h5>
                                                <p className="text-sm text-gray-600 mb-2">En la sección "Headers" de Agora, añade:</p>
                                                <div className="space-y-2">
                                                    <div className="bg-gray-100 p-2 rounded">
                                                        <span className="text-xs text-gray-600">Header:</span>
                                                        <code className="text-sm font-mono text-gray-800 ml-2">x-tpv-api-key</code>
                                                    </div>
                                                    <div className="bg-gray-100 p-2 rounded">
                                                        <span className="text-xs text-gray-600">Valor:</span>
                                                        <code className="text-sm font-mono text-gray-800 ml-2">[Usa la API Key de abajo]</code>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Paso 4 */}
                                            <div className="bg-white p-4 rounded-lg border">
                                                <h5 className="font-medium text-gray-800 mb-2">Paso 4: Activar el Webhook</h5>
                                                <p className="text-sm text-gray-600">
                                                    Marca la casilla <strong>"Activar Webhook"</strong> y guarda la configuración.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Flujo de trabajo */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-green-800 mb-3">Flujo de Trabajo</h4>
                                <div className="space-y-2 text-sm text-green-700">
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                                        <span>Cliente presenta ticket con QR en la tienda</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                                        <span>TPV escanea el QR del ticket</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                                        <span>Agora procesa la transacción y envía webhook</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-green-200 rounded-full flex items-center justify-center text-xs font-bold">4</span>
                                        <span>Cliente acumula puntos automáticamente</span>
                                    </div>
                                </div>
                            </div>

                            {/* Estado de configuraciones */}
                            {configurations.length > 0 ? (
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-800 mb-3">Configuraciones Activas</h4>
                                    <div className="space-y-3">
                                        {configurations.map((config) => (
                                            <div key={config._id} className="p-4 bg-gray-50 rounded-lg border">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <p className="font-medium text-gray-800">{config.nombre}</p>
                                                        <p className="text-sm text-gray-600">
                                                            {config.tipoSoftware} • {config.activo ? 'Activo' : 'Inactivo'}
                                                        </p>
                                                    </div>
                                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.activo
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {config.activo ? 'Activo' : 'Inactivo'}
                                                    </span>
                                                </div>

                                                {/* API Key para esta configuración */}
                                                <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                                                    <p className="text-xs text-yellow-800 mb-2 font-medium">🔑 API Key para Agora Software</p>
                                                    <p className="text-xs text-yellow-700 mb-2">
                                                        Copia esta API Key y úsala en el Paso 3 de arriba como valor del header "x-tpv-api-key":
                                                    </p>
                                                    {config.apiKey ? (
                                                        <div className="flex items-center gap-2">
                                                            <code className="text-sm font-mono text-gray-800 bg-white px-2 py-1 rounded flex-1 border">
                                                                {config.apiKey}
                                                            </code>
                                                            <button
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(config.apiKey);
                                                                    // Aquí podrías añadir una notificación
                                                                }}
                                                                className="px-3 py-1 bg-yellow-600 text-white text-xs rounded hover:bg-yellow-700 transition-colors font-medium"
                                                                title="Copiar API Key"
                                                            >
                                                                📋 Copiar
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="bg-orange-50 p-3 rounded border border-orange-200">
                                                            <p className="text-xs text-orange-800 mb-2">
                                                                🔑 API Key no disponible en esta consulta
                                                            </p>
                                                            <p className="text-xs text-orange-700 mb-2">
                                                                La API Key se almacena de forma encriptada en la base de datos por seguridad.
                                                                Haz clic en "Obtener API Key" para desencriptarla y mostrarla.
                                                            </p>
                                                            <div className="bg-white p-2 rounded border">
                                                                <p className="text-xs text-gray-600 mb-2">Para obtener la API Key:</p>
                                                                <div className="space-y-2">
                                                                    <button
                                                                        className="w-full px-3 py-2 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors font-medium"
                                                                        onClick={async () => {
                                                                            try {
                                                                                // Mostrar loading
                                                                                Swal.fire({
                                                                                    title: 'Obteniendo API Key...',
                                                                                    text: 'Por favor espera',
                                                                                    allowOutsideClick: false,
                                                                                    didOpen: () => {
                                                                                        Swal.showLoading();
                                                                                    }
                                                                                });

                                                                                // Intentar obtener la API Key usando el nuevo endpoint
                                                                                const result = await tpvConfigurations.getApiKey(config._id);

                                                                                if (result && result.apiKey) {
                                                                                    // Mostrar la API Key
                                                                                    Swal.fire({
                                                                                        title: 'API Key Obtenida',
                                                                                        html: `
                                                                            <div class="text-left">
                                                                                <p class="mb-3">API Key para <strong>${config.nombre}</strong>:</p>
                                                                                <div class="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                                                                    <p class="text-sm font-medium text-yellow-800 mb-2">🔑 API Key:</p>
                                                                                    <div class="bg-white p-2 rounded border">
                                                                                        <code class="text-sm font-mono text-gray-800 break-all">${result.apiKey}</code>
                                                                                    </div>
                                                                                    <p class="text-xs text-yellow-700 mt-2">
                                                                                        Guarda esta clave de forma segura. La necesitarás para configurar Agora Software.
                                                                                    </p>
                                                                                    ${result.generatedAt ? `
                                                                                        <p class="text-xs text-gray-600 mt-2">
                                                                                            Generada: ${new Date(result.generatedAt).toLocaleString()}
                                                                                        </p>
                                                                                    ` : ''}
                                                                                </div>
                                                                            </div>
                                                                        `,
                                                                                        icon: 'success',
                                                                                        confirmButtonText: 'Copiar API Key',
                                                                                        preConfirm: () => {
                                                                                            navigator.clipboard.writeText(result.apiKey);
                                                                                        }
                                                                                    });

                                                                                    // Actualizar la configuración local para mostrar la API Key
                                                                                    setConfigurations(prev =>
                                                                                        prev.map(c =>
                                                                                            c._id === config._id
                                                                                                ? { ...c, apiKey: result.apiKey }
                                                                                                : c
                                                                                        )
                                                                                    );
                                                                                } else {
                                                                                    console.error('No API Key in response:', result);
                                                                                    Swal.fire({
                                                                                        title: 'API Key No Disponible',
                                                                                        html: `
                                                                            <div class="text-left">
                                                                                <p class="mb-3">No se pudo obtener la API Key de esta configuración.</p>
                                                                                <div class="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                                                                    <p class="text-sm font-medium text-blue-800 mb-2">💡 Solución:</p>
                                                                                    <p class="text-sm text-blue-700 mb-3">
                                                                                        Ve a la pestaña <strong>"Configuración"</strong> y crea una nueva configuración TPV. 
                                                                                        La API Key se mostrará solo al crear una nueva configuración.
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        `,
                                                                                        icon: 'info',
                                                                                        confirmButtonText: 'Ir a Configuración',
                                                                                        preConfirm: () => {
                                                                                            setActiveTab('config');
                                                                                        }
                                                                                    });
                                                                                }
                                                                            } catch (error) {
                                                                                console.error('Error getting API key:', error);
                                                                                Swal.fire({
                                                                                    title: 'Error',
                                                                                    text: 'Error al obtener la API Key. Intenta crear una nueva configuración.',
                                                                                    icon: 'error',
                                                                                    confirmButtonText: 'Ir a Configuración',
                                                                                    preConfirm: () => {
                                                                                        setActiveTab('config');
                                                                                    }
                                                                                });
                                                                            }
                                                                        }}
                                                                    >
                                                                        🔑 Obtener API Key
                                                                    </button>
                                                                    <p className="text-xs text-gray-500 text-center">
                                                                        O elimina y crea una nueva configuración
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Información adicional */}
                                                {config.descripcion && (
                                                    <p className="text-sm text-gray-600 mt-2">{config.descripcion}</p>
                                                )}

                                                {config.ipAddress && (
                                                    <p className="text-xs text-gray-500 mt-1">
                                                        IP: {config.ipAddress}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 text-2xl" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-800 mb-2">No hay configuraciones TPV</h3>
                                    <p className="text-gray-600 mb-4">Crea tu primera configuración TPV para comenzar a usar el sistema</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {activeTab === 'stats' && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Estadísticas TPV</h3>

                        {stats ? (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                        <FontAwesomeIcon icon={faCog} className="text-blue-600 mr-3" />
                                        <div>
                                            <p className="text-sm text-blue-600">Total Configuraciones</p>
                                            <p className="text-2xl font-bold text-blue-800">{stats.totalConfigurations}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-3" />
                                        <div>
                                            <p className="text-sm text-green-600">Configuraciones Activas</p>
                                            <p className="text-2xl font-bold text-green-800">{stats.activeConfigurations}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                        <FontAwesomeIcon icon={faChartBar} className="text-purple-600 mr-3" />
                                        <div>
                                            <p className="text-sm text-purple-600">Total Requests</p>
                                            <p className="text-2xl font-bold text-purple-800">{stats.totalRequests}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FontAwesomeIcon icon={faChartBar} className="text-gray-400 text-2xl" />
                                </div>
                                <p className="text-gray-600">Las estadísticas estarán disponibles una vez que tengas configuraciones TPV activas</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TpvManager;